<template>
  <div class="access-log">
    <!-- gate -->
    <div v-if="!log" class="gate">
      <div class="gate-card">
        <div class="wordmark">P V I S</div>
        <div class="gate-title">Board Review — Access Log</div>
        <div class="gate-sub">ADMINISTRATOR ONLY</div>
        <form @submit.prevent="load">
          <label class="gate-label" for="al-email">Email</label>
          <input id="al-email" v-model="email" type="email" required class="gate-input" :disabled="loading" />
          <label class="gate-label" for="al-pw">Board password</label>
          <input id="al-pw" v-model="password" type="password" required class="gate-input" :disabled="loading" />
          <label class="gate-label" for="al-key">Admin key</label>
          <input id="al-key" v-model="adminKey" type="password" required class="gate-input" :disabled="loading" />
          <button type="submit" class="gate-btn" :disabled="loading || !email || !password || !adminKey">
            {{ loading ? 'CHECKING…' : 'OPEN THE LOG' }}
          </button>
        </form>
        <p v-if="error" class="gate-error">{{ error }}</p>
        <p class="gate-note">The admin key is separate from the board password (Vercel env BOARD_REVIEW_ADMIN_KEY).</p>
      </div>
    </div>

    <!-- log -->
    <template v-else>
      <header class="topbar">
        <div class="topbar-id">
          <span class="wordmark">P V I S</span>
          <span class="topbar-title">Board Review — Access Log</span>
        </div>
        <div class="topbar-actions">
          <button class="tb-btn" @click="load">Refresh</button>
          <button class="tb-btn" @click="close">Close</button>
        </div>
      </header>

      <section class="sections-admin">
        <div class="sa-head">
          <span class="sa-title">BOARD PAGE SECTIONS</span>
          <span class="sa-sub">Untick a section to hide it from everyone on /board-review. Applies on their next load.</span>
        </div>
        <div class="sa-grid">
          <label v-for="s in SECTION_DEFS" :key="s.id" class="sa-item">
            <input type="checkbox" v-model="sections[s.id]" />
            <span>{{ s.label }}</span>
          </label>
        </div>
        <div class="sa-actions">
          <button class="tb-btn tb-btn-solid" :disabled="savingSections" @click="saveSections">
            {{ savingSections ? 'Saving…' : 'Save section visibility' }}
          </button>
          <span v-if="sectionsMsg" class="sa-msg">{{ sectionsMsg }}</span>
        </div>
      </section>

      <section class="summary">
        <div><span class="s-label">RECORDS</span><span class="s-val">{{ log.length }}</span></div>
        <div><span class="s-label">UNIQUE EMAILS</span><span class="s-val">{{ uniqueEmails }}</span></div>
        <div><span class="s-label">FAILED ATTEMPTS</span><span class="s-val" :class="{ hot: failures > 0 }">{{ failures }}</span></div>
        <div><span class="s-label">LAST ACCESS</span><span class="s-val s-small">{{ log.length ? fmtTs(log[0].ts) : '—' }}</span></div>
      </section>

      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th class="left">Time (local)</th>
              <th class="left">Email</th>
              <th>Result</th>
              <th class="left">IP</th>
              <th class="left">Platform</th>
              <th class="left">Timezone</th>
              <th class="left">Screen</th>
              <th class="left">Browser</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in log" :key="i" :class="{ fail: !r.success }">
              <td class="left nowrap">{{ fmtTs(r.ts) }}</td>
              <td class="left">{{ r.email }}</td>
              <td :class="r.success ? 'ok' : 'ko'">{{ r.success ? 'OK' : 'FAIL' }}</td>
              <td class="left nowrap">{{ r.ip }}</td>
              <td class="left">{{ r.platform || '—' }}</td>
              <td class="left">{{ r.timezone || '—' }}</td>
              <td class="left">{{ r.screen || '—' }}</td>
              <td class="left ua" :title="r.userAgent">{{ shortUa(r.userAgent) }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="foot-note">Last 500 records, newest first. Every attempt on /board-review is logged, including failures.</p>
    </template>
  </div>
</template>

<script>
import axios from 'axios';

const SECTION_DEFS = [
  { id: 'staffpct', label: 'Staff cost vs benchmark & ceiling' },
  { id: 'income', label: 'Income, expenses and what is left over' },
  { id: 'groups', label: 'Where the money goes' },
  { id: 'breakeven', label: 'What it would take to pay for it' },
  { id: 'tornado', label: 'Which lever moves the result' },
  { id: 'scenarios', label: 'Scenario comparison' },
  { id: 'parity', label: 'Teacher pay vs Australian parity' },
  { id: 'ratios', label: 'Students per staff member' },
  { id: 'principal', label: 'Principal pay — premium and parity' },
  { id: 'leadershipavg', label: 'Leadership team average' },
  { id: 'fees', label: 'Fee position' },
  { id: 'bands', label: 'Proposed 2027 salary bands' },
];

export default {
  name: 'BoardAccessLog',
  data() {
    return {
      email: '', password: '', adminKey: '', loading: false, error: '', log: null,
      SECTION_DEFS,
      sections: Object.fromEntries(SECTION_DEFS.map((s) => [s.id, true])),
      savingSections: false,
      sectionsMsg: '',
    };
  },
  computed: {
    uniqueEmails() { return new Set(this.log.map((r) => r.email)).size; },
    failures() { return this.log.filter((r) => !r.success).length; },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = '';
      try {
        const res = await axios.post('/api/boardReview', {
          email: this.email.trim(),
          password: this.password,
          action: 'accessLog',
          adminKey: this.adminKey,
        });
        this.log = res.data.log || [];
        const saved = res.data.sections || {};
        for (const s of SECTION_DEFS) this.sections[s.id] = saved[s.id] !== false;
      } catch (e) {
        if (e.response && e.response.status === 401) this.error = 'Incorrect board password.';
        else if (e.response && e.response.status === 403) this.error = 'Incorrect admin key.';
        else if (e.response && e.response.status === 400) this.error = e.response.data.error;
        else this.error = 'Could not reach the server.';
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.log = null;
      this.password = '';
      this.adminKey = '';
    },
    async saveSections() {
      this.savingSections = true;
      this.sectionsMsg = '';
      try {
        await axios.post('/api/boardReview', {
          email: this.email.trim(),
          password: this.password,
          action: 'setSections',
          adminKey: this.adminKey,
          sections: { ...this.sections },
        });
        const hidden = SECTION_DEFS.filter((s) => !this.sections[s.id]).length;
        this.sectionsMsg = hidden ? `Saved — ${hidden} section${hidden === 1 ? '' : 's'} hidden.` : 'Saved — all sections visible.';
      } catch (e) {
        this.sectionsMsg = 'Save failed — check the connection and try again.';
      } finally {
        this.savingSections = false;
      }
    },
    fmtTs(ts) {
      return new Date(ts).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
    },
    shortUa(ua) {
      if (!ua) return '—';
      if (/iphone|ipad/i.test(ua)) return 'iOS Safari';
      if (/android/i.test(ua)) return 'Android';
      if (/edg\//i.test(ua)) return 'Edge';
      if (/chrome\//i.test(ua)) return 'Chrome';
      if (/safari\//i.test(ua)) return 'Safari';
      if (/firefox\//i.test(ua)) return 'Firefox';
      return ua.slice(0, 24) + '…';
    },
  },
};
</script>

<style scoped>
.access-log {
  --paper: #ffffff; --ink: #201e1d; --muted: #6f6b66; --hairline: #e3e1dc;
  --track: #eeece7; --red: #ec3013; --good: #159615;
  min-height: 100vh; background: var(--paper); color: var(--ink);
  font-family: 'Helvetica Neue', Helvetica, Arial, system-ui, sans-serif;
  text-align: left;
}
.wordmark { font-weight: 800; letter-spacing: 0.28em; font-size: 15px; }

.gate { min-height: 100vh; display: flex; align-items: center; justify-content: center; padding: 24px; background: #faf9f5; }
.gate-card { background: var(--paper); border: 1.5px solid var(--ink); padding: 40px 36px; max-width: 400px; width: 100%; }
.gate-title { font-size: 20px; font-weight: 800; margin-top: 14px; }
.gate-sub { font-size: 10px; letter-spacing: 0.12em; color: var(--muted); margin: 6px 0 22px; }
.gate-label { display: block; font-size: 10px; font-weight: 600; letter-spacing: 0.1em; text-transform: uppercase; color: var(--muted); margin: 14px 0 4px; }
.gate-input { width: 100%; box-sizing: border-box; padding: 10px 12px; font-size: 14px; border: 1px solid var(--ink); border-radius: 0; background: var(--paper); color: var(--ink); }
.gate-input:focus { outline: 2px solid var(--red); outline-offset: -1px; }
.gate-btn { width: 100%; margin-top: 22px; padding: 12px; font-size: 12px; font-weight: 700; letter-spacing: 0.1em; border: none; border-radius: 0; background: var(--ink); color: #fff; cursor: pointer; }
.gate-btn:disabled { opacity: 0.5; cursor: default; }
.gate-error { color: var(--red); font-size: 13px; margin-top: 14px; font-weight: 600; }
.gate-note { color: var(--muted); font-size: 10.5px; margin-top: 20px; line-height: 1.5; }

.topbar { display: flex; justify-content: space-between; align-items: center; gap: 12px; padding: 12px 24px; border-bottom: 1.5px solid var(--ink); flex-wrap: wrap; }
.topbar-id { display: flex; align-items: baseline; gap: 16px; }
.topbar-title { font-size: 15px; font-weight: 700; }
.topbar-actions { display: flex; gap: 8px; }
.tb-btn { background: none; border: 1px solid var(--ink); color: var(--ink); border-radius: 0; padding: 6px 12px; font-size: 11px; font-weight: 600; cursor: pointer; }
.tb-btn:hover { background: var(--track); }

.sections-admin { padding: 14px 24px 16px; border-bottom: 1.5px solid var(--ink); background: #faf9f5; }
.sa-head { display: flex; align-items: baseline; gap: 14px; flex-wrap: wrap; margin-bottom: 10px; }
.sa-title { font-size: 10px; font-weight: 700; letter-spacing: 0.14em; }
.sa-sub { font-size: 11px; color: var(--muted); }
.sa-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 6px 18px; }
.sa-item { display: flex; align-items: center; gap: 8px; font-size: 12.5px; cursor: pointer; }
.sa-item input { accent-color: var(--red); width: 14px; height: 14px; }
.sa-actions { display: flex; align-items: center; gap: 14px; margin-top: 12px; }
.tb-btn-solid { background: var(--ink); color: #fff; }
.tb-btn:disabled { opacity: 0.5; cursor: default; }
.sa-msg { font-size: 12px; font-weight: 600; color: var(--good); }

.summary { display: flex; gap: 40px; padding: 16px 24px; border-bottom: 1px solid var(--hairline); flex-wrap: wrap; }
.summary > div { display: flex; flex-direction: column; }
.s-label { font-size: 9.5px; font-weight: 700; letter-spacing: 0.12em; color: var(--muted); margin-bottom: 4px; }
.s-val { font-size: 22px; font-weight: 800; }
.s-val.hot { color: var(--red); }
.s-small { font-size: 14px; font-weight: 700; padding-top: 5px; }

.table-wrap { overflow-x: auto; padding: 0 24px; }
table { width: 100%; border-collapse: collapse; font-size: 12px; min-width: 900px; }
th { text-align: right; font-weight: 600; color: var(--muted); font-size: 10px; letter-spacing: 0.08em; text-transform: uppercase; padding: 10px 10px 6px; border-bottom: 1.5px solid var(--ink); white-space: nowrap; }
td { text-align: right; padding: 7px 10px; border-bottom: 1px solid var(--hairline); font-variant-numeric: tabular-nums; }
.left { text-align: left; }
.nowrap { white-space: nowrap; }
.ok { color: var(--good); font-weight: 700; }
.ko { color: var(--red); font-weight: 700; }
tr.fail td { background: #fdf1ef; }
.ua { max-width: 160px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.foot-note { padding: 12px 24px 40px; font-size: 10.5px; color: var(--muted); }
@media (max-width: 700px) {
  .summary { gap: 22px; }
  .table-wrap { padding: 0 12px; }
}
</style>
